
import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useTimetableActions } from '../../hooks/useTimetableActions';
import { useAttendanceStore } from '../../store/attendanceStore';
import { toast } from 'sonner';
import ClassStatusForm from './ClassStatusForm';

const ClassDetailsDialog = ({ isOpen, onClose, classData }) => {
  const [activeTab, setActiveTab] = useState('details');
  const { updateClassStatus, deleteClass } = useTimetableActions();
  const [isDeleting, setIsDeleting] = useState(false);
  const [teacherAttendance, setTeacherAttendance] = useState(null);
  const { getDailyAttendance } = useAttendanceStore();
  
  useEffect(() => {
    if (!classData?.teacherId) return;
    
    // Get teacher's attendance for the day of this class
    const classDate = parseISO(classData.startTime);
    const dateStr = format(classDate, 'yyyy-MM-dd');
    const dailyAttendance = getDailyAttendance(dateStr);
    
    const teacherRecord = dailyAttendance.find(
      record => record.teacherId === classData.teacherId
    );
    
    setTeacherAttendance(teacherRecord || null);
  }, [classData, getDailyAttendance]);
  
  if (!classData) return null;
  
  const handleStatusChange = async (newStatus) => {
    try {
      await updateClassStatus(classData.teacherId, classData.id, newStatus);
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteClass(classData.teacherId, classData.id);
      toast.success('Class deleted successfully');
      onClose();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Failed to delete class');
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{classData.subject}</DialogTitle>
          <DialogDescription>View and manage class details</DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="status">Status</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-1">
                <div className="text-sm font-medium">Teacher:</div>
                <div className="text-sm col-span-2">{classData.teacherName}</div>
              </div>
              
              <div className="grid grid-cols-3 gap-1">
                <div className="text-sm font-medium">Date:</div>
                <div className="text-sm col-span-2">
                  {format(parseISO(classData.startTime), "EEEE, MMMM d, yyyy")}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-1">
                <div className="text-sm font-medium">Time:</div>
                <div className="text-sm col-span-2">
                  {format(parseISO(classData.startTime), "h:mm a")} - {format(parseISO(classData.endTime), "h:mm a")}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-1">
                <div className="text-sm font-medium">Room:</div>
                <div className="text-sm col-span-2">{classData.room}</div>
              </div>
              
              <div className="grid grid-cols-3 gap-1">
                <div className="text-sm font-medium">Status:</div>
                <div className={`text-sm col-span-2 capitalize ${
                  classData.status === 'absent' ? 'text-red-600 font-medium' : 
                  classData.status === 'late' ? 'text-yellow-600 font-medium' : 
                  'text-green-600 font-medium'
                }`}>
                  {classData.status}
                </div>
              </div>
              
              {teacherAttendance && (
                <div className="grid grid-cols-3 gap-1">
                  <div className="text-sm font-medium">Teacher:</div>
                  <div className={`text-sm col-span-2 capitalize ${
                    teacherAttendance.status === 'absent' ? 'text-red-600 font-medium' : 
                    teacherAttendance.status === 'late' ? 'text-yellow-600 font-medium' : 
                    'text-green-600 font-medium'
                  }`}>
                    {teacherAttendance.status}
                    {teacherAttendance.hasSubstitute && teacherAttendance.substituteName && (
                      <span className="ml-2 text-blue-500">
                        (Substitute: {teacherAttendance.substituteName})
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {classData.notes && (
                <div className="mt-4">
                  <div className="text-sm font-medium">Notes:</div>
                  <div className="text-sm mt-1 bg-muted p-2 rounded">{classData.notes}</div>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex justify-between items-center mt-6">
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Class'}
              </Button>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </TabsContent>
          
          <TabsContent value="status" className="space-y-4 mt-4">
            <ClassStatusForm 
              currentStatus={classData.status}
              onSubmit={handleStatusChange}
              onCancel={() => setActiveTab('details')}
            />
            
            <DialogFooter className="flex justify-end items-center mt-6">
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ClassDetailsDialog;
